/**
 * Foundation · Timesheet — cella giustificativi (causale + durata) di una giornata.
 * Le causali disponibili sono passate dal modulo chiamante (nessuna dipendenza da GI Presenze).
 */
import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DurationInput, DurationFormatBadge, formatDuration, useDurationFormat } from "@/foundation/duration";

function EmptyTrigger({ suggest, remainingMinutes, fmt }) {
  if (suggest) return <span className="rounded border border-dashed border-amber-400 px-1.5 text-amber-700">+ giustifica {formatDuration(remainingMinutes / 60, fmt)}</span>;
  return <span className="text-suite-neutral-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">+ giustificativo</span>;
}

export function JustificationCell({ day, options, remainingMinutes, readOnly, suggest, onChange, testid }) {
  const fmt = useDurationFormat();
  const [open, setOpen] = useState(false);
  const list = day.justifications || [];
  const label = (code) => options.find((o) => o.code === code)?.short || code;
  const set = (i, patch) => onChange(list.map((j, k) => (k === i ? { ...j, ...patch } : j)));
  const add = () => onChange([...list, { cause: options[0]?.code || "", minutes: Math.max(0, remainingMinutes) || null }]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={readOnly && !list.length} data-testid={testid}
          className="w-full min-h-[28px] rounded px-1.5 text-left text-[11px] font-mono hover:bg-suite-primary-light focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-suite-primary">
          {list.length
            ? list.map((j, i) => <span key={i} className="mr-1 inline-block rounded bg-amber-50 border border-amber-200 px-1 text-amber-800">{label(j.cause)} {j.minutes ? formatDuration(j.minutes / 60, fmt) : "?"}</span>)
            : !readOnly && <EmptyTrigger suggest={suggest} remainingMinutes={remainingMinutes} fmt={fmt} />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-3 space-y-2" align="start" data-testid={`${testid}-popover`}>
        <div className="flex items-center justify-between text-xs font-semibold text-suite-neutral-700">
          Giustificativi <DurationFormatBadge data-testid={`${testid}-format-badge`} />
        </div>
        {list.map((j, i) => (
          <div key={i} className="flex items-center gap-2">
            <select value={j.cause} disabled={readOnly} onChange={(e) => set(i, { cause: e.target.value })}
              className="h-8 flex-1 rounded-md border border-input bg-transparent px-2 text-xs" data-testid={`${testid}-cause-${i}`}>
              <option value="">Causale…</option>
              {options.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
            </select>
            <DurationInput className="h-8 w-20 text-xs" value={j.minutes ? j.minutes / 60 : null} readOnly={readOnly}
              onCommit={(h) => set(i, { minutes: h ? Math.round(h * 60) : null })} data-testid={`${testid}-hours-${i}`} />
            {!readOnly && (
              <button type="button" aria-label="Rimuovi giustificativo" onClick={() => onChange(list.filter((_, k) => k !== i))}
                className="text-suite-neutral-400 hover:text-suite-error" data-testid={`${testid}-remove-${i}`}><Trash2 size={14} /></button>
            )}
          </div>
        ))}
        {!readOnly && (
          <button type="button" onClick={add} className="inline-flex items-center gap-1 text-xs text-suite-primary hover:underline" data-testid={`${testid}-add`}>
            <Plus size={12} /> Aggiungi {remainingMinutes > 0 ? `(resto giornata ${formatDuration(remainingMinutes / 60, fmt)})` : ""}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default JustificationCell;
